import React from 'react';
import { Alert, Button, Card, Empty, List, Space, Tag, Typography, message } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import {
  ESTADO_INDISPONIVEL,
  MENSAGEM_INDISPONIVEL,
  corEstadoPagamentoPortal,
  descricaoTipoDocumentoPortal,
  formatarDataVencimentoPortal,
  formatarItemPagamentoPortal,
  formatarValorMonetarioPortal,
  labelEstadoPagamentoPortal,
  pagamentoPodeAbrirBoleto,
  pagamentoPodeCopiarLinhaDigitavel,
} from './pagamentosEventosPortal';

const { Text } = Typography;

// Fase E.3 -- sub-aba "Pagamentos" do Portal do Responsavel -> Eventos
// ("como e quando eu pago", nunca "quanto ficou" -- isso e' E.2,
// MinhasInscricoesList.js). Componente "burro" (mesmo principio de
// MinhasInscricoesList.js/EventosDisponiveisList.js): recebe `pagamentos`
// ja prontos do backend (evento_cobranca_portal_service.py via GET
// /api/portal/eventos/pagamentos) e SO apresenta -- nenhum calculo
// financeiro, nenhuma interpretacao de status tecnico, nenhuma
// autorizacao decidida aqui.
//
// READ-ONLY estrutural: as UNICAS 2 acoes possiveis (Abrir boleto/
// Copiar linha digitavel) sao 100% client-side -- `window.open`/
// `navigator.clipboard.writeText`, ZERO chamada HTTP, ZERO mutacao.
// Nenhum botao de Emitir/Reemitir/Cancelar/Verificar/Consolidar/
// Dissolver existe nem deve ser adicionado aqui -- isso e' exclusivo
// do Admin (CobrancasEventosAdmin.js).
const PagamentosEventosList = ({ pagamentos = [], loading, erro }) => {
  const handleAbrirBoleto = (url) => {
    const janela = window.open(url, '_blank', 'noopener,noreferrer');
    if (!janela) {
      message.warning('Não foi possível abrir o boleto. Verifique bloqueio de pop-up.');
    }
  };

  const handleCopiarLinhaDigitavel = async (linhaDigitavel) => {
    try {
      await navigator.clipboard.writeText(linhaDigitavel);
      message.success('Linha digitável copiada.');
    } catch (err) {
      message.error('Não foi possível copiar a linha digitável.');
    }
  };

  return (
    <Card title="Pagamentos" bordered={false} style={{ marginBottom: 24 }}>
      {erro && <Alert type="error" message={erro} showIcon closable style={{ marginBottom: 16 }} />}
      <List
        dataSource={pagamentos}
        loading={loading}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum pagamento de evento encontrado." />
          ),
        }}
        renderItem={(pagamento) => {
          const podeAbrirBoleto = pagamentoPodeAbrirBoleto(pagamento);
          const podeCopiarLinha = pagamentoPodeCopiarLinhaDigitavel(pagamento);
          return (
            <List.Item key={pagamento.documento_id}>
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8 }}>
                    <Text strong>{pagamento.aluno_nome || '—'}</Text>
                    <Tag color={corEstadoPagamentoPortal(pagamento.estado)}>
                      {labelEstadoPagamentoPortal(pagamento.estado)}
                    </Tag>
                  </div>
                }
                description={
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">Vencimento: {formatarDataVencimentoPortal(pagamento.vencimento) || '—'}</Text>
                    <Text>Valor: {formatarValorMonetarioPortal(pagamento.valor) || '—'}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {descricaoTipoDocumentoPortal(pagamento)}
                    </Text>

                    {pagamento.itens && pagamento.itens.length > 0 && (
                      <div style={{ marginTop: 2 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Inclui:
                        </Text>
                        {pagamento.itens.map((item, index) => (
                          <div key={`${pagamento.documento_id}-item-${index}`}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {formatarItemPagamentoPortal(item)}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}

                    {pagamento.estado === ESTADO_INDISPONIVEL && (
                      <Alert type="warning" showIcon message={MENSAGEM_INDISPONIVEL} style={{ padding: '2px 10px' }} />
                    )}

                    {(podeAbrirBoleto || podeCopiarLinha) && (
                      <Space size={8} wrap style={{ marginTop: 4 }}>
                        {podeAbrirBoleto && (
                          <Button
                            size="small"
                            icon={<LinkOutlined />}
                            onClick={() => handleAbrirBoleto(pagamento.url_boleto)}
                          >
                            Abrir boleto
                          </Button>
                        )}
                        {podeCopiarLinha && (
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopiarLinhaDigitavel(pagamento.linha_digitavel)}
                          >
                            Copiar linha digitável
                          </Button>
                        )}
                      </Space>
                    )}
                  </Space>
                }
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
};

export default PagamentosEventosList;
